import { AlertTriangle, DollarSign, Gift, TrendingDown, Users } from 'lucide-react';
import { useState } from 'react';
import { StatCard } from '../components/StatCard';
import {
  type FreeCapacitySnapshot,
  useFreeCapacity,
  useUpdateFreeCapacityPolicy,
} from '../hooks/useAdminData';

const usd = (value: number) =>
  value.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });

const usdPrecise = (value: number) =>
  value.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 4 });

/**
 * Why the engine refused to fund anything. Each of these is a missing input,
 * never a computed zero — the distinction matters because one is fixed by
 * configuration and the other by selling more.
 */
const BLOCKED_COPY: Record<NonNullable<FreeCapacitySnapshot['blockedReason']>, string> = {
  costing_untrustworthy:
    'Model prices are missing from platform settings, so every execution is costed at zero. Capacity cannot be derived from that — seed the pricing table first.',
  no_cost_baseline:
    'No free organization has produced a measurable cost yet, so there is no price for a slot. Capacity stays closed until one exists.',
  negative_margin:
    'The paying organizations cost more than they bring in this period, so there is no margin to reinvest.',
};

function Row({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-2.5 border-b border-border last:border-0">
      <div>
        <span className="text-sm text-text-secondary">{label}</span>
        {hint ? <p className="text-xs text-text-muted mt-0.5">{hint}</p> : null}
      </div>
      <span className="text-sm font-medium text-text-primary tabular-nums whitespace-nowrap">
        {value}
      </span>
    </div>
  );
}

type NumericPolicyKey = Exclude<keyof FreeCapacitySnapshot['policy'], 'enforceSignupGate'>;
type PolicyDraft = Record<NumericPolicyKey, string>;

const MAX_POLICY_WINDOW_DAYS = 3650;

interface PolicyField {
  key: NumericPolicyKey;
  label: string;
  hint: string;
  step: string;
  min?: number;
  max?: number;
}

const CAPACITY_FIELDS: PolicyField[] = [
  {
    key: 'giveawayShare',
    label: 'Reinvested share',
    hint: '0 to 1. The fraction of the margin that pays for free access.',
    step: '0.05',
  },
  {
    key: 'costPercentile',
    label: 'Cost percentile',
    hint: '1 to 100. Higher prices a slot more strictly, funding fewer of them.',
    step: '5',
  },
  {
    key: 'floor',
    label: 'Floor',
    hint: 'Slots kept open even when the margin cannot pay for them.',
    step: '1',
  },
  {
    key: 'hardCap',
    label: 'Hard cap',
    hint: 'The computed figure can never exceed this.',
    step: '1',
  },
];

const LIFECYCLE_FIELDS: PolicyField[] = [
  {
    key: 'inviteExpiryDays',
    min: 1,
    max: MAX_POLICY_WINDOW_DAYS,
    label: 'Invitation expiry (days)',
    hint: 'How long an invitation stays open before the place returns to the queue.',
    step: '1',
  },
  {
    key: 'inactivityWarnAfterDays',
    min: 1,
    max: MAX_POLICY_WINDOW_DAYS,
    label: 'Inactivity warning after (days)',
    hint: 'Days of no meaningful use before the holder is nudged.',
    step: '1',
  },
  {
    key: 'reminderGraceDays',
    min: 1,
    max: MAX_POLICY_WINDOW_DAYS,
    label: 'Reminder grace (days)',
    hint: 'Days after the nudge before the place is reclaimed.',
    step: '1',
  },
  {
    key: 'promoteBatchSize',
    label: 'Promotion batch size',
    hint: 'How many places are offered at once when capacity frees up.',
    step: '1',
  },
];

const FIELDS: PolicyField[] = [...CAPACITY_FIELDS, ...LIFECYCLE_FIELDS];

function PolicyFieldset({
  legend,
  fields,
  draft,
  onChange,
}: {
  legend: string;
  fields: PolicyField[];
  draft: PolicyDraft;
  onChange: (key: NumericPolicyKey, value: string) => void;
}) {
  return (
    <fieldset className="min-w-0 border-0 p-0 m-0">
      <legend className="text-xs font-medium uppercase tracking-wide text-text-muted mb-3">
        {legend}
      </legend>
      <div className="grid gap-4 sm:grid-cols-2">
        {fields.map(({ key, label, hint, step, min, max }) => (
          <label key={key} className="block">
            <span className="text-sm text-text-secondary">{label}</span>
            <input
              type="number"
              step={step}
              min={min}
              max={max}
              value={draft[key]}
              aria-label={label}
              onChange={(event) => onChange(key, event.target.value)}
              className="mt-1 w-full rounded-lg border border-border bg-surface-hover px-3 py-2 text-sm text-text-primary"
            />
            <span className="text-xs text-text-muted mt-1 block">{hint}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

function PolicyEditor({ policy }: { policy: FreeCapacitySnapshot['policy'] }) {
  const update = useUpdateFreeCapacityPolicy();
  // Seeded once. The saved policy is the source of truth, and the parent
  // remounts this form under a new key when it changes, so a landed save wins
  // over whatever is still sitting in the inputs without an effect syncing them.
  const [draft, setDraft] = useState<PolicyDraft>(
    () => Object.fromEntries(FIELDS.map(({ key }) => [key, String(policy[key])])) as PolicyDraft,
  );

  const [enforce, setEnforce] = useState(policy.enforceSignupGate);

  const dirty =
    FIELDS.some(({ key }) => draft[key] !== String(policy[key])) ||
    enforce !== policy.enforceSignupGate;

  const setField = (key: NumericPolicyKey, value: string) =>
    setDraft((prev) => ({ ...prev, [key]: value }));

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    update.mutate({
      ...(Object.fromEntries(FIELDS.map(({ key }) => [key, Number(draft[key])])) as Record<
        NumericPolicyKey,
        number
      >),
      enforceSignupGate: enforce,
    });
  };

  return (
    <form onSubmit={submit} className="bg-surface rounded-xl border border-border p-5">
      <h2 className="text-sm font-semibold text-text-primary mb-1">Policy</h2>
      <p className="text-xs text-text-muted mb-4">
        The budget figures decide how much money is given away; saving re-runs the arithmetic above.
        The waitlist figures decide how long a place is held before it moves on.
      </p>

      <div className="space-y-6">
        <PolicyFieldset
          legend="Budget"
          fields={CAPACITY_FIELDS}
          draft={draft}
          onChange={setField}
        />
        <PolicyFieldset
          legend="Waitlist"
          fields={LIFECYCLE_FIELDS}
          draft={draft}
          onChange={setField}
        />
      </div>

      {/* The gate ships switched off. Turning it on is the moment the number
          above stops being a report and starts turning people away. */}
      <label className="flex items-start gap-3 mt-4 rounded-lg border border-border p-3">
        <input
          type="checkbox"
          checked={enforce}
          aria-label="Turn away signups when no slot is left"
          onChange={(event) => setEnforce(event.target.checked)}
          className="mt-0.5"
        />
        <span>
          <span className="text-sm text-text-secondary">
            Turn away signups when no slot is left
          </span>
          <span className="text-xs text-text-muted mt-0.5 block">
            While this is off the board only reports. With it on, a signup with no slot left is sent
            to the waitlist instead of getting an organization. Invited users are never affected.
          </span>
        </span>
      </label>

      <div className="flex items-center gap-3 mt-4">
        <button
          type="submit"
          disabled={!dirty || update.isPending}
          className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white disabled:opacity-40"
        >
          {update.isPending ? 'Saving…' : 'Save policy'}
        </button>
        {update.isError ? (
          <span className="text-sm text-danger">
            {(update.error as { response?: { data?: { message?: string } } })?.response?.data
              ?.message ?? 'The policy was rejected.'}
          </span>
        ) : null}
      </div>
    </form>
  );
}

export function FreeCapacity() {
  const { data, isLoading, error } = useFreeCapacity();

  if (error) {
    return (
      <div className="p-6">
        <p className="text-sm text-danger">Could not load the capacity snapshot.</p>
      </div>
    );
  }

  const share = data ? Math.round(data.policy.giveawayShare * 100) : 0;
  const reserved = data?.reservedByInvitations ?? 0;

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-text-primary">Free tier capacity</h1>
        <p className="text-sm text-text-muted mt-1">
          How many organizations the current margin can carry on the free plan, and the figures
          behind that number.
        </p>
      </div>

      {data?.blockedReason ? (
        <div className="flex gap-3 rounded-xl border border-warning/40 bg-warning/10 p-4">
          <AlertTriangle className="h-5 w-5 shrink-0 text-warning" />
          <div>
            <p className="text-sm font-medium text-text-primary">No capacity is being funded</p>
            <p className="text-sm text-text-secondary mt-0.5">{BLOCKED_COPY[data.blockedReason]}</p>
          </div>
        </div>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Available slots"
          value={data?.availableSlots ?? 0}
          icon={Gift}
          loading={isLoading}
          caption={
            reserved > 0
              ? `${data?.occupiedSlots ?? 0} of ${data?.allowedSlots ?? 0} taken, ${reserved} held by open invitations`
              : `${data?.occupiedSlots ?? 0} of ${data?.allowedSlots ?? 0} allowed already taken`
          }
        />
        <StatCard
          label="Monthly margin"
          value={data ? usd(data.marginUsd) : '—'}
          icon={data && data.marginUsd < 0 ? TrendingDown : DollarSign}
          loading={isLoading}
          caption={`${share}% of it funds free access`}
        />
        <StatCard
          label="Cost per free org"
          value={data ? usdPrecise(data.costPerFreeOrgUsd) : '—'}
          icon={Users}
          loading={isLoading}
          caption={`p${data?.policy.costPercentile ?? 75} over ${data?.costWindowDays ?? 30} days`}
        />
        <StatCard
          label="Free organizations"
          value={data?.freeOrgCount ?? 0}
          icon={Users}
          loading={isLoading}
          caption="currently on the free plan"
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <section className="bg-surface rounded-xl border border-border p-5">
          <h2 className="text-sm font-semibold text-text-primary mb-3">How the budget is built</h2>
          {data ? (
            <>
              <Row
                label="Recurring revenue"
                value={usd(data.monthlyRevenueUsd)}
                hint="catalogue price of the active paid subscriptions"
              />
              <Row
                label="Cost of serving them"
                value={`− ${usd(data.paidOrgCostUsd)}`}
                hint={`measured over the last ${data.costWindowDays} days`}
              />
              <Row label="Margin" value={usd(data.marginUsd)} />
              <Row
                label={`Reinvested (${share}%)`}
                value={usd(data.giveawayBudgetUsd)}
                hint="the budget free access is paid from"
              />
            </>
          ) : (
            <p className="text-sm text-text-muted">Loading…</p>
          )}
        </section>

        <section className="bg-surface rounded-xl border border-border p-5">
          <h2 className="text-sm font-semibold text-text-primary mb-3">How a slot is priced</h2>
          {data ? (
            <>
              <Row
                label={`Cost per free org (p${data.policy.costPercentile})`}
                value={usdPrecise(data.costPerFreeOrgUsd)}
                hint="a percentile, so one runaway organization cannot swing the count"
              />
              <Row
                label="Average, for comparison"
                value={usdPrecise(data.meanFreeOrgCostUsd)}
                hint="shown to make the skew visible, not used in the maths"
              />
              <Row label="Funded slots" value={String(data.fundedSlots)} />
              <Row
                label="Held by open invitations"
                value={String(reserved)}
                hint="invited from the waitlist and not signed up yet; kept until the invitation expires"
              />
              <Row
                label="Floor / hard cap"
                value={`${data.policy.floor} / ${data.policy.hardCap}`}
                hint="the floor holds even when the margin cannot pay for it"
              />
            </>
          ) : (
            <p className="text-sm text-text-muted">Loading…</p>
          )}
        </section>
      </div>

      {/* Keyed on the saved policy so a landed save remounts the form with the
          new values, instead of an effect writing over the operator's inputs. */}
      {data ? <PolicyEditor key={JSON.stringify(data.policy)} policy={data.policy} /> : null}

      {/* Previously a muted one-liner below the form, which read as absent when
          it had nothing to list. Stated either way now: "no plans are unpriced"
          and "this panel was never built" are different facts about the money,
          and only one of them needs acting on. */}
      {data ? (
        <section className="bg-surface rounded-xl border border-border p-5">
          <h2 className="text-sm font-semibold text-text-primary mb-1">
            Plans contributing no revenue
          </h2>
          {data.unpricedPlans.length > 0 ? (
            <>
              <p className="text-sm text-text-secondary">
                {data.unpricedPlans.map((plan) => (
                  <span
                    key={plan}
                    className="inline-block rounded-md bg-surface-hover px-2 py-0.5 mr-2 font-medium"
                  >
                    {plan}
                  </span>
                ))}
              </p>
              <p className="text-xs text-text-muted mt-2">
                Their price is negotiated and no contract value is recorded, so they add nothing to
                the margin above. Assuming a figure would fund free access out of money the platform
                has not been promised.
              </p>
            </>
          ) : (
            <p className="text-sm text-text-muted">
              Every active paid plan carries a catalogue price, so the whole margin above is
              accounted for.
            </p>
          )}
        </section>
      ) : null}
    </div>
  );
}
